<script lang="ts" setup>
import { TechDataVehicle } from '@/types';
import TechDataModal from '@/components/tech-data-modal.vue';
import { computed, ref } from 'vue';
import { getDefaultLocaleValue } from '../locales/default';
import { useTracking } from '../composables/useTracking';
import { vHasScroll } from '../directives/v-has-scroll';

type TransmissionMode = 'manual' | 'automatic';
type Weights = Partial<
  Record<TransmissionMode, { weightMax?: { value?: number } }>
>;

const props = defineProps<{
  translations: Record<string, string>;
  vehicle: TechDataVehicle;
  vehicles: TechDataVehicle[];
  locale: string;
  modelSeries: string;
  validOptions: {
    weights: number[] | null;
    roofs: string[] | null;
    cabins: string[] | null;
    drivetrains: string[] | null;
    batteryCapacities: number[] | null;
    engines: string[] | null;
  };
  transmissionMode: TransmissionMode | null;
}>();

const isOpen = ref(false);

// Tracking
const { trackModalButton } = useTracking(props.modelSeries);

// Track modal open button
function handleOpenModal() {
  trackModalButton('all_technical_data', props.vehicle);
  isOpen.value = true;
}

const t = (key: string, fallbackKey: string) =>
  props.translations[key] ?? getDefaultLocaleValue(fallbackKey);

const formatWithUnit = (value?: number | null, unit?: string | null) => {
  if (value == null) return '';
  const formatted = value.toLocaleString(props.locale);
  return unit ? `${formatted} ${unit}` : formatted;
};

const mode = computed(() => props.transmissionMode?.toLocaleLowerCase());

const TRIM_RANK: Record<string, number> = {
  basic: 0,
  base: 1,
  pro: 2,
  select: 3,
};

function trimRank(trim: unknown) {
  const key = String(trim ?? '').toLowerCase();
  return TRIM_RANK[key] ?? 999;
}

const filteredVehicles = computed(() => {
  const m = mode.value as keyof Weights | undefined;
  const baseFiltered = props.vehicles.filter((v) => {
    const engineMatch = v.engineType === props.vehicle.engineType;
    const drivetrainMatch = v.drivetrainType === props.vehicle.drivetrainType;
    const batteryMatch = v.batteryCapacity === props.vehicle.batteryCapacity;
    const weightMatch =
      m == null ||
      v.weights?.[m]?.weightMax?.value ===
        props.vehicle.weights?.[m]?.weightMax?.value;
    const cabinMatch = v.cabinType === props.vehicle.cabinType;
    const roofMatch = v.roofHeightType === props.vehicle.roofHeightType;

    return (
      engineMatch &&
      drivetrainMatch &&
      batteryMatch &&
      weightMatch &&
      cabinMatch &&
      roofMatch
    );
  });

  const byEngine = new Map<string, any>();

  for (const v of baseFiltered) {
    const key = v.engine?.engineName ?? v.engineType ?? '';
    const current = byEngine.get(key);

    if (!current || trimRank(v.trim) < trimRank(current.trim)) {
      byEngine.set(key, v);
    }
  }

  return Array.from(byEngine.values()).sort((a, b) => {
    const aName = a.engine?.engineName ?? '';
    const bName = b.engine?.engineName ?? '';
    return aName.localeCompare(bName);
  });
});
</script>

<template>
  <div class="table-container" v-has-scroll>
    <table class="wbx-table wbx-table--striped">
      <thead>
        <tr>
          <th class="sticky-cell" scope="col"></th>
          <th
            v-for="(vehicle, index) in filteredVehicles"
            :key="index"
            scope="col"
          >
            {{ vehicle.engine?.engineName || '—' }}
          </th>
        </tr>
      </thead>

      <tbody class="wbx-body">
        <tr>
          <th class="sticky-cell" scope="row">
            {{
              props.translations['owvc_tech_data_performance'] ||
              getDefaultLocaleValue('performance')
            }}
          </th>
          <td v-for="(vehicle, index) in filteredVehicles" :key="index">
            <span v-if="vehicle.engine?.kwPower && vehicle.engine?.hpPower">
              {{ vehicle.engine.kwPower.value }} kW ({{
                vehicle.engine.hpPower.value
              }}
              PS)
            </span>

            <!-- Fallback for empty vehicle objects -->
            <span v-else>—</span>
          </td>
        </tr>

        <tr>
          <th class="sticky-cell" scope="row">
            {{
              props.translations['owvc_tech_data_emissions_co2_combined'] ||
              getDefaultLocaleValue('emissions_co2_combined')
            }}
          </th>
          <td v-for="(vehicle, index) in filteredVehicles" :key="index">
            {{
              mode
                ? (vehicle.emissionsConsumption?.[mode]?.emissions
                    ?.individual ?? '-')
                : '-'
            }}
            g/km
          </td>
        </tr>

        <tr>
          <th class="sticky-cell" scope="row">
            {{
              t('owvc_tech_data_consumption_combined', 'consumption_combined')
            }}
          </th>
          <td v-for="(vehicle, index) in filteredVehicles" :key="index">
            {{
              (() => {
                const m = mode ?? 'manual';
                const data = vehicle.emissionsConsumption?.[m]?.consumption;
                return data ? `${data.individual} ${data.unit}` : '—';
              })()
            }}
          </td>
        </tr>

        <tr>
          <th class="sticky-cell" scope="row">
            {{ t('owvc_tech_data_body_seats', 'body_seats') }}
          </th>
          <td v-for="(vehicle, index) in filteredVehicles" :key="index">
            {{ `${vehicle.body?.seats?.value}` }}
          </td>
        </tr>

        <tr v-if="vehicle.loadingSpaceDimensions?.lengthMax?.value">
          <th class="sticky-cell" scope="row">
            {{
              t(
                'owvc_tech_data_loadingSpaceDimensions_lengthMax',
                'loadingSpaceDimensions_lengthMax',
              )
            }}
          </th>
          <td v-for="(vehicle, index) in filteredVehicles" :key="index">
            {{
              formatWithUnit(
                vehicle.loadingSpaceDimensions?.lengthMax?.value,
                vehicle.loadingSpaceDimensions?.lengthMax?.unit,
              )
            }}
          </td>
        </tr>

        <tr v-if="vehicle.loadingSpaceDimensions?.heightMax?.value">
          <th class="sticky-cell" scope="row">
            {{
              t(
                'owvc_tech_data_loadingSpaceDimensions_heightMax',
                'loadingSpaceDimensions_heightMax',
              )
            }}
          </th>
          <td v-for="(vehicle, index) in filteredVehicles" :key="index">
            {{
              formatWithUnit(
                vehicle.loadingSpaceDimensions?.heightMax?.value,
                vehicle.loadingSpaceDimensions?.heightMax?.unit,
              )
            }}
          </td>
        </tr>

        <tr>
          <th class="sticky-cell" scope="row">
            {{ t('owvc_tech_data_weights_payloadMax', 'weights_payloadMax') }}
          </th>
          <td v-for="(vehicle, index) in filteredVehicles" :key="index">
            {{
              (() => {
                const m = mode ?? 'manual';
                const w = vehicle.weights?.[m]?.payloadMax;

                return w && w.value != null
                  ? formatWithUnit(w.value, w.unit)
                  : '—';
              })()
            }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="wbx-button-wrapper">
    <button
      class="wbx-button wbx-button--secondary wbx-button--large"
      data-test-id="owvans-tech-data-modal-open-button"
      style="flex: 0 1 auto"
      @click="handleOpenModal"
    >
      {{ t('owvc_tech_data_all_technical_data', 'allTechnicalData') }}
    </button>
  </div>

  <tech-data-modal
    :translations="translations"
    :locale="locale"
    :vehicle="vehicle"
    :vehicles="filteredVehicles"
    :validOptions="validOptions"
    :isOpen="isOpen"
    :modelSeries="modelSeries"
    :transmissionMode="transmissionMode"
    @close="isOpen = false"
  />
</template>

<style lang="scss" scoped>
@import '@workbench/core/dist/scss/utility.scss';

.table-container {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  max-width: 100%;
  display: block;
  scrollbar-width: thin;
  margin-bottom: 32px;
}

.wbx-table {
  width: 100%;
  margin-top: 32px;
  overflow: visible;
}

.wbx-table th {
  padding-top: 24px;
  padding-bottom: 24px;
}

.wbx-table td {
  white-space: nowrap;
}

.wbx-table th,
.wbx-table td {
  text-align: right;
}

.wbx-button-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
}

.wbx-table .sticky-cell {
  position: sticky;
  left: 0;
  z-index: 2;
  min-width: min(40vw, 210px);
  text-align: left;
}

.v-has-scroll-x .sticky-cell::after {
  content: '';
  position: absolute;
  inset: -5px 3px -5px 0;
  box-shadow: 4px 0 4px -4px var(--wb-table-border-color);
  opacity: 0.8;
}

.wbx-table thead .sticky-cell {
  background: #fff;
}

.wbx-table tbody tr:nth-child(odd) .sticky-cell {
  background: var(--table-background-striped-color);
}

.wbx-table tbody tr:nth-child(even) .sticky-cell {
  background: #fff;
}
</style>
